// Import transactional templates
import confirmation from "./transactional/confirmation";
import eventRegistration from "./transactional/eventRegistration";
import paymentConfirmation from "./transactional/paymentConfirmation";
import pendingPayment from "./transactional/pendingPayment";
import paymentReceiptPremium from "./transactional/paymentReceiptPremium";

// Import marketing templates
import newsletter from "./marketing/newsletter";
import promotion from "./marketing/promotion";

// Import onboarding templates
import welcome from "./onboarding/welcome";

// Import event templates
import invitation from "./events/invitation";

// Import Product templates
import productLaunch from "./product/productLaunch";
import productDemo from "./product/productDemo";
import productReminder from "./product/productReminder";
import productFeedback from "./product/productFeedback";
import productAnnouncement from "./product/productAnnouncement";
import productSurvey from "./product/productSurvey";
import productUpsell from "./product/productUpsell";
import productUpdate from "./product/productUpdate";

// Import Pulse templates
import weeklyDigest from "./pulse/weeklyDigest";
import monthlyAnalytics from "./pulse/monthlyAnalytics";
import performanceReport from "./pulse/performanceReport";
import usageReport from "./pulse/usageReport";
import billingReport from "./pulse/billingReport";

// Import Sequence templates
import onboardingDay1 from "./sequences/onboardingDay1";
import onboardingDay3 from "./sequences/onboardingDay3";
import nurtureLead from "./sequences/nurtureLead";
import reEngagement from "./sequences/reEngagement";
import trialExpiring from "./sequences/trialExpiring";

// Import Cold Outreach templates
import coldOutreachTemplates, {
  COLD_OUTREACH_TEMPLATES,
  COLD_OUTREACH_TEMPLATES_ARRAY,
} from "./coldOutreach";

/**
 * Helper function to fix social elements content in MJML templates
 * Ensures proper display of social media names
 *
 * @param content The parsed JSON content
 * @returns The fixed content with proper social element names
 */
export const fixSocialElements = (content: any) => {
  const traverse = (node: any) => {
    // Fix social elements that have undefined content
    if (
      node.type === "social" &&
      node.data &&
      node.data.value &&
      Array.isArray(node.data.value.elements)
    ) {
      node.data.value.elements.forEach((element: any) => {
        if (!element.content || element.content === "undefined") {
          // Map names to proper display content
          const nameMap: Record<string, string> = {
            Facebook: "Facebook",
            facebook: "Facebook",
            Twitter: "Twitter",
            twitter: "Twitter",
            Instagram: "Instagram",
            instagram: "Instagram",
            LinkedIn: "LinkedIn",
            linkedin: "LinkedIn",
          };
          element.content = nameMap[element.name] || element.name || "Social";
        }
      });
    }

    // Recursively traverse children
    if (node.children && node.children.length) {
      node.children.forEach(traverse);
    }
  };

  // Create a deep copy to avoid modifying the original
  const clonedContent = JSON.parse(JSON.stringify(content));
  traverse(clonedContent);
  return clonedContent;
};

// Define template categories
export const TEMPLATE_CATEGORIES = {
  all: "All Templates",
  marketing: "Marketing",
  onboarding: "Onboarding",
  events: "Events",
  transactional: "Transactional",
  product: "Product",
  pulse: "Pulse Reports",
  sequences: "Email Sequences",
  coldOutreach: "Cold Outreach",
};

// Export all transactional templates in a category object
export const TRANSACTIONAL_TEMPLATES = {
  confirmation,
  eventRegistration,
  paymentConfirmation,
  pendingPayment,
  paymentReceiptPremium,
};

// Export all marketing templates in a category object
export const MARKETING_TEMPLATES = {
  newsletter,
  promotion,
};

// Export all onboarding templates in a category object
export const ONBOARDING_TEMPLATES = {
  welcome,
};

// Export all event templates in a category object
export const EVENT_TEMPLATES = {
  invitation,
};

// Export all product templates in a category object
export const PRODUCT_TEMPLATES = {
  productLaunch,
  productDemo,
  productReminder,
  productFeedback,
  productAnnouncement,
  productSurvey,
  productUpsell,
  productUpdate,
};

// Export all pulse templates in a category object
export const PULSE_TEMPLATES = {
  weeklyDigest,
  monthlyAnalytics,
  performanceReport,
  usageReport,
  billingReport,
};

// Export all sequence templates in a category object
export const SEQUENCE_TEMPLATES = {
  onboardingDay1,
  onboardingDay3,
  nurtureLead,
  reEngagement,
  trialExpiring,
};

// Re-export cold outreach templates
export { COLD_OUTREACH_TEMPLATES, COLD_OUTREACH_TEMPLATES_ARRAY };

// Export templates as array for the gallery - includes all categories
export const TEMPLATES_ARRAY = [
  // Transactional templates
  confirmation,
  eventRegistration,
  paymentConfirmation,
  pendingPayment,
  paymentReceiptPremium,

  // Marketing templates
  newsletter,
  promotion,

  // Onboarding templates
  welcome,

  // Event templates
  invitation,

  // Product templates
  productLaunch,
  productDemo,
  productReminder,
  productFeedback,
  productAnnouncement,
  productSurvey,
  productUpsell,
  productUpdate,

  // Pulse templates
  weeklyDigest,
  monthlyAnalytics,
  performanceReport,
  usageReport,
  billingReport,

  // Sequence templates
  onboardingDay1,
  onboardingDay3,
  nurtureLead,
  reEngagement,
  trialExpiring,

  // Cold Outreach templates
  ...COLD_OUTREACH_TEMPLATES_ARRAY,
];

// Export all product templates in a category object
export const PRODUCT_TEMPLATES_ARRAY = [
  productLaunch,
  productDemo,
  productReminder,
  productFeedback,
  productAnnouncement,
  productSurvey,
  productUpsell,
  productUpdate,
];

// Export all pulse templates as array
export const PULSE_TEMPLATES_ARRAY = [
  weeklyDigest,
  monthlyAnalytics,
  performanceReport,
  usageReport,
  billingReport,
];

// Export all sequence templates as array
export const SEQUENCE_TEMPLATES_ARRAY = [
  onboardingDay1,
  onboardingDay3,
  nurtureLead,
  reEngagement,
  trialExpiring,
];

// Helper function to get a template by ID
export const getTemplateById = (id: string) => {
  return TEMPLATES_ARRAY.find((template) => template.id === id);
};

// Helper function to get templates by category
export const getTemplatesByCategory = (category: string) => {
  if (category === "all") {
    return TEMPLATES_ARRAY;
  }
  return TEMPLATES_ARRAY.filter((template) => template.category === category);
};

// Export individual transactional templates
export {
  confirmation,
  eventRegistration,
  paymentConfirmation,
  pendingPayment,
  paymentReceiptPremium,
};

// Export individual marketing templates
export { newsletter, promotion };

// Export individual onboarding templates
export { welcome };

// Export individual event templates
export { invitation };

// Export individual product templates
export {
  productLaunch,
  productDemo,
  productReminder,
  productFeedback,
  productUpsell,
  productAnnouncement,
  productSurvey,
  productUpdate,
};

// Export individual pulse templates
export {
  weeklyDigest,
  monthlyAnalytics,
  performanceReport,
  usageReport,
  billingReport,
};

// Export individual sequence templates
export {
  onboardingDay1,
  onboardingDay3,
  nurtureLead,
  reEngagement,
  trialExpiring,
};

// Export default object for easier imports
export default {
  getAllTemplates: () => TEMPLATES_ARRAY,
  getTemplateById,
  getTemplatesByCategory,
  categories: TEMPLATE_CATEGORIES,
  transactional: TRANSACTIONAL_TEMPLATES,
  marketing: MARKETING_TEMPLATES,
  onboarding: ONBOARDING_TEMPLATES,
  events: EVENT_TEMPLATES,
  product: PRODUCT_TEMPLATES,
  pulse: PULSE_TEMPLATES,
  sequences: SEQUENCE_TEMPLATES,
  coldOutreach: COLD_OUTREACH_TEMPLATES,
};
